import { useMemo, useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Formik } from 'formik';
import Grid from '@mui/material/Grid';
import MKBox from 'components/MaterialKit/MKBox';
import MKTypography from 'components/MaterialKit/MKTypography';
import InputField, { INPUT_TYPE_SELECT } from 'components/InputField';
import { getLocaleMap } from 'utils/locales';
import { useNavigate } from 'react-router-dom';
import Image from 'components/Image';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import DoneIcon from '@mui/icons-material/Done';
import { Autocomplete, TextField } from '@mui/material';
import map from 'lodash/map';
import sortBy from 'lodash/sortBy';
import { getUserIWatchAccounts } from 'api/iwatch';
import { useAuth } from 'contexts/auth';
import { handleErrorResponse } from 'utils/general';
import { multiWordfilterOptions } from 'components/AutoComplete';
import { parsePhoneNumberFromString, ParseError } from 'libphonenumber-js/max';

const EditMonitorForm = ({ monitorItem, models, onSave, onDelete, titleFontSize, titleColor, ...props }) => {
  const [lm, setLm] = useState({});
  useEffect(() => {
    getLocaleMap([
      'errors_required',
    ])
      .then((response) => {
        setLm(response);
      });
  }, []);

  const getFormValidation = (values) => {
    const { model, currency, expected_lowest_price, expected_highest_price } = values;
    const errors = {};
    if (!model) {
      errors.model = lm.errors_required;
    }
    if (expected_lowest_price && Number.isNaN(Number(expected_lowest_price))) {
      errors.expected_lowest_price = 'Value is not a Number';
    }
    if (expected_highest_price && Number.isNaN(Number(expected_highest_price))) {
      errors.expected_highest_price = 'Value is not a Number';
    }
    return errors;
  };

  const { auth, setAuth } = useAuth();

  const navigate = useNavigate();
  const [brandName, setBrandName] = useState();
  const [modelImage, setModelImage] = useState();
  const [sourceAccountOptions, setSourceAccountOptions] = useState([
    { label: '(none)', value: null },
  ]);

  const fetchUserIWatchAccountsFromApi = useCallback(() => {
    if (!auth?.user) {
      return Promise.resolve();
    }
    return getUserIWatchAccounts({ $expand: 'iwatch_account', user: auth?.user?.username })
      .then(({ data }) => {
        const iWatchAccounts = sortBy(map(data, 'iwatch_account'), ['sequence']);
        const newSourceAccountOptions = map(iWatchAccounts, (iWatchAccount) => {
          const accountName = iWatchAccount.name;
          const phone = iWatchAccount.phone;
          try {
            const phoneNumber = parsePhoneNumberFromString('+' + phone);
            const formattedPhoneNumber = phoneNumber && phoneNumber.formatInternational() || phone;
            return {
              label: `${iWatchAccount.key} (${accountName}) (${formattedPhoneNumber})`,
              value: accountName,
            };
          } catch (e) {
            if (e instanceof ParseError) {
              // Not a phone number, non-existent country, etc.
              console.log(e.message);
            } else {
              throw e;
            }
          }
        });

        newSourceAccountOptions.unshift({ label: '(none)', value: null });
        setSourceAccountOptions(newSourceAccountOptions);
      })
      .catch((err) => {
        handleErrorResponse(err, setAuth);
      });
  }, [auth, setAuth]);

  useEffect(() => {
    fetchUserIWatchAccountsFromApi();
  }, [fetchUserIWatchAccountsFromApi]);

  const [autoCompleteValue, setAutoCompleteValue] = useState(null);

  const initialValues = useMemo(() => ({
    model: monitorItem?.model?.model_id || null,
    condition: monitorItem?.condition || 'all',
    monitor_type: monitorItem?.monitor_type || 'sell',
    currency: monitorItem?.currency || null,
    expected_lowest_price: monitorItem?.expected_lowest_price || null,
    expected_highest_price: monitorItem?.expected_highest_price || null,
    source_account: monitorItem?.source_account || null,
  }), [monitorItem]);

  const modelOptions = useMemo(() => (models || []).map((model) => ({
    id_code: model.id_code,
    id_name: model.id_name,
    value: model.model_id,
  })), [models]);

  useEffect(() => {
    if (modelOptions && initialValues) {
      setAutoCompleteValue(modelOptions.find((option) => option.value === initialValues.model));
    }
  }, [initialValues, modelOptions]);

  const conditionOptions = [
    { label: 'All', value: 'all' },
    { label: 'New', value: 'new' },
    { label: 'Used', value: 'used' },
  ];

  const monitorTypeOptions = [
    { label: 'Selling', value: 'sell' },
    { label: 'Looking', value: 'buy' },
  ];

  const currencyOptions = [
    { label: '(none)', value: null },
    {'label': 'HKD', 'value': 'HKD'},
    {'label': 'USD', 'value': 'USD'},
    {'label': 'EUR', 'value': 'EUR'},
    {'label': 'CNY', 'value': 'CNY'},
    {'label': 'JPY', 'value': 'JPY'},
    {'label': 'USDT', 'value': 'USDT'},
    {'label': 'AED', 'value': 'AED'},
    {'label': 'AFN', 'value': 'AFN'},
    {'label': 'AMD', 'value': 'AMD'},
    {'label': 'AOA', 'value': 'AOA'},
    {'label': 'ARS', 'value': 'ARS'},
    {'label': 'AUD', 'value': 'AUD'},
    {'label': 'AWG', 'value': 'AWG'},
    {'label': 'AZN', 'value': 'AZN'},
    {'label': 'BAM', 'value': 'BAM'},
    {'label': 'BBD', 'value': 'BBD'},
    {'label': 'BDT', 'value': 'BDT'},
    {'label': 'BGN', 'value': 'BGN'},
    {'label': 'BHD', 'value': 'BHD'},
    {'label': 'BIF', 'value': 'BIF'},
    {'label': 'BMD', 'value': 'BMD'},
    {'label': 'BND', 'value': 'BND'},
    {'label': 'BOB', 'value': 'BOB'},
    {'label': 'BOV', 'value': 'BOV'},
    {'label': 'BRL', 'value': 'BRL'},
    {'label': 'BSD', 'value': 'BSD'},
    {'label': 'BTN', 'value': 'BTN'},
    {'label': 'BWP', 'value': 'BWP'},
    {'label': 'BYN', 'value': 'BYN'},
    {'label': 'BZD', 'value': 'BZD'},
    {'label': 'CAD', 'value': 'CAD'},
    {'label': 'CDF', 'value': 'CDF'},
    {'label': 'CHE', 'value': 'CHE'},
    {'label': 'CHF', 'value': 'CHF'},
    {'label': 'CHW', 'value': 'CHW'},
    {'label': 'CLF', 'value': 'CLF'},
    {'label': 'CLP', 'value': 'CLP'},
    {'label': 'COP', 'value': 'COP'},
    {'label': 'COU', 'value': 'COU'},
    {'label': 'CRC', 'value': 'CRC'},
    {'label': 'CUP', 'value': 'CUP'},
    {'label': 'CVE', 'value': 'CVE'},
    {'label': 'CZK', 'value': 'CZK'},
    {'label': 'DJF', 'value': 'DJF'},
    {'label': 'DKK', 'value': 'DKK'},
    {'label': 'DOP', 'value': 'DOP'},
    {'label': 'DZD', 'value': 'DZD'},
    {'label': 'EGP', 'value': 'EGP'},
    {'label': 'ERN', 'value': 'ERN'},
    {'label': 'ETB', 'value': 'ETB'},
    {'label': 'FJD', 'value': 'FJD'},
    {'label': 'FKP', 'value': 'FKP'},
    {'label': 'GBP', 'value': 'GBP'},
    {'label': 'GEL', 'value': 'GEL'},
    {'label': 'GHS', 'value': 'GHS'},
    {'label': 'GIP', 'value': 'GIP'},
    {'label': 'GMD', 'value': 'GMD'},
    {'label': 'GNF', 'value': 'GNF'},
    {'label': 'GTQ', 'value': 'GTQ'},
    {'label': 'GYD', 'value': 'GYD'},
    {'label': 'HNL', 'value': 'HNL'},
    {'label': 'HTG', 'value': 'HTG'},
    {'label': 'HUF', 'value': 'HUF'},
    {'label': 'IDR', 'value': 'IDR'},
    {'label': 'ILS', 'value': 'ILS'},
    {'label': 'INR', 'value': 'INR'},
    {'label': 'IQD', 'value': 'IQD'},
    {'label': 'IRR', 'value': 'IRR'},
    {'label': 'ISK', 'value': 'ISK'},
    {'label': 'JMD', 'value': 'JMD'},
    {'label': 'JOD', 'value': 'JOD'},
    {'label': 'KES', 'value': 'KES'},
    {'label': 'KGS', 'value': 'KGS'},
    {'label': 'KHR', 'value': 'KHR'},
    {'label': 'KMF', 'value': 'KMF'},
    {'label': 'KPW', 'value': 'KPW'},
    {'label': 'KRW', 'value': 'KRW'},
    {'label': 'KWD', 'value': 'KWD'},
    {'label': 'KYD', 'value': 'KYD'},
    {'label': 'KZT', 'value': 'KZT'},
    {'label': 'LAK', 'value': 'LAK'},
    {'label': 'LBP', 'value': 'LBP'},
    {'label': 'LKR', 'value': 'LKR'},
    {'label': 'LRD', 'value': 'LRD'},
    {'label': 'LSL', 'value': 'LSL'},
    {'label': 'LYD', 'value': 'LYD'},
    {'label': 'MAD', 'value': 'MAD'},
    {'label': 'MDL', 'value': 'MDL'},
    {'label': 'MGA', 'value': 'MGA'},
    {'label': 'MKD', 'value': 'MKD'},
    {'label': 'MMK', 'value': 'MMK'},
    {'label': 'MNT', 'value': 'MNT'},
    {'label': 'MOP', 'value': 'MOP'},
    {'label': 'MRU', 'value': 'MRU'},
    {'label': 'MUR', 'value': 'MUR'},
    {'label': 'MVR', 'value': 'MVR'},
    {'label': 'MWK', 'value': 'MWK'},
    {'label': 'MXN', 'value': 'MXN'},
    {'label': 'MXV', 'value': 'MXV'},
    {'label': 'MYR', 'value': 'MYR'},
    {'label': 'MZN', 'value': 'MZN'},
    {'label': 'NAD', 'value': 'NAD'},
    {'label': 'NGN', 'value': 'NGN'},
    {'label': 'NIO', 'value': 'NIO'},
    {'label': 'NOK', 'value': 'NOK'},
    {'label': 'NPR', 'value': 'NPR'},
    {'label': 'NZD', 'value': 'NZD'},
    {'label': 'OMR', 'value': 'OMR'},
    {'label': 'PAB', 'value': 'PAB'},
    {'label': 'PEN', 'value': 'PEN'},
    {'label': 'PGK', 'value': 'PGK'},
    {'label': 'PHP', 'value': 'PHP'},
    {'label': 'PKR', 'value': 'PKR'},
    {'label': 'PLN', 'value': 'PLN'},
    {'label': 'PYG', 'value': 'PYG'},
    {'label': 'QAR', 'value': 'QAR'},
    {'label': 'RON', 'value': 'RON'},
    {'label': 'RSD', 'value': 'RSD'},
    {'label': 'RUB', 'value': 'RUB'},
    {'label': 'RWF', 'value': 'RWF'},
    {'label': 'SAR', 'value': 'SAR'},
    {'label': 'SBD', 'value': 'SBD'},
    {'label': 'SCR', 'value': 'SCR'},
    {'label': 'SDG', 'value': 'SDG'},
    {'label': 'SEK', 'value': 'SEK'},
    {'label': 'SGD', 'value': 'SGD'},
    {'label': 'SHP', 'value': 'SHP'},
    {'label': 'SLE', 'value': 'SLE'},
    {'label': 'SOS', 'value': 'SOS'},
    {'label': 'SRD', 'value': 'SRD'},
    {'label': 'SSP', 'value': 'SSP'},
    {'label': 'STN', 'value': 'STN'},
    {'label': 'SVC', 'value': 'SVC'},
    {'label': 'SYP', 'value': 'SYP'},
    {'label': 'SZL', 'value': 'SZL'},
    {'label': 'THB', 'value': 'THB'},
    {'label': 'TJS', 'value': 'TJS'},
    {'label': 'TMT', 'value': 'TMT'},
    {'label': 'TND', 'value': 'TND'},
    {'label': 'TOP', 'value': 'TOP'},
    {'label': 'TRY', 'value': 'TRY'},
    {'label': 'TTD', 'value': 'TTD'},
    {'label': 'TWD', 'value': 'TWD'},
    {'label': 'TZS', 'value': 'TZS'},
    {'label': 'UAH', 'value': 'UAH'},
    {'label': 'UGX', 'value': 'UGX'},
    {'label': 'USN', 'value': 'USN'},
    {'label': 'UYI', 'value': 'UYI'},
    {'label': 'UYU', 'value': 'UYU'},
    {'label': 'UYW', 'value': 'UYW'},
    {'label': 'UZS', 'value': 'UZS'},
    {'label': 'VED', 'value': 'VED'},
    {'label': 'VES', 'value': 'VES'},
    {'label': 'VND', 'value': 'VND'},
    {'label': 'VUV', 'value': 'VUV'},
    {'label': 'WST', 'value': 'WST'},
    {'label': 'XAD', 'value': 'XAD'},
    {'label': 'XAF', 'value': 'XAF'},
    {'label': 'XAG', 'value': 'XAG'},
    {'label': 'XAU', 'value': 'XAU'},
    {'label': 'XBA', 'value': 'XBA'},
    {'label': 'XBB', 'value': 'XBB'},
    {'label': 'XBC', 'value': 'XBC'},
    {'label': 'XBD', 'value': 'XBD'},
    {'label': 'XCD', 'value': 'XCD'},
    {'label': 'XCG', 'value': 'XCG'},
    {'label': 'XDR', 'value': 'XDR'},
    {'label': 'XOF', 'value': 'XOF'},
    {'label': 'XPD', 'value': 'XPD'},
    {'label': 'XPF', 'value': 'XPF'},
    {'label': 'XPT', 'value': 'XPT'},
    {'label': 'XSU', 'value': 'XSU'},
    {'label': 'XTS', 'value': 'XTS'},
    {'label': 'XUA', 'value': 'XUA'},
    {'label': 'XXX', 'value': 'XXX'},
    {'label': 'YER', 'value': 'YER'},
    {'label': 'ZAR', 'value': 'ZAR'},
    {'label': 'ZMW', 'value': 'ZMW'},
    {'label': 'ZWG', 'value': 'ZWG'},
  ];

  const updateNameAndImage = useCallback((modelId) => {
    const selectedModel = (models || []).find((model) => model.model_id === modelId);
    setBrandName(selectedModel?.brand_name);
    setModelImage(selectedModel?.default_image?.file_url);
  }, [models]);

  const handleChangeModel = useCallback((modelId, setFieldValue) => {
    setFieldValue('model', modelId);
    updateNameAndImage(modelId);
  }, [updateNameAndImage]);

  useEffect(() => {
    if (initialValues.model) {
      updateNameAndImage(initialValues.model);
    }
  }, [initialValues, updateNameAndImage]);

  const handleClickOption = useCallback((attribute, value, setFieldValue) => {
    setFieldValue(attribute, value);
  }, []);

  return (
    <Formik
      onSubmit={onSave}
      initialValues={initialValues}
      validate={getFormValidation}
      enableReinitialize
      {...props}
    >
      {({ handleChange, handleBlur, handleSubmit, setFieldValue, setFieldError, errors, values, touched, isSubmitting, dirty }) => {
        return (
          <MKBox component="form" role="form">
            <MKBox display="flex" alignItems="center" width="100%" marginBottom="1rem">
              <MKBox display="flex" flex={1}>
                <ArrowBackIosNewIcon fontSize="medium" style={{ cursor: 'pointer', color: titleColor }} onClick={() => navigate('/config_monitors')} />
              </MKBox>
              <MKBox display="flex" flex={2}>
                <MKTypography width="100%" color={titleColor} fontSize={titleFontSize} textAlign="center">
                  {monitorItem ? 'Monitor Details' : 'Add Monitor'}
                </MKTypography>
              </MKBox>
              <MKBox display="flex" justifyContent="flex-end" flex={1}>
                <DoneIcon fontSize="large" style={{ cursor: 'pointer', color: titleColor }} onClick={handleSubmit} />
              </MKBox>
            </MKBox>
            <MKBox>
              <MKTypography fontSize="18px" color="#000">
                Source Feeder Account (Phone Number)
              </MKTypography>
              <InputField
                name="source_account"
                label=""
                value={values.source_account}
                type={INPUT_TYPE_SELECT}
                setFieldValue={setFieldValue}
                options={sourceAccountOptions}
                invalid={touched.source_account && !!errors.source_account}
                sx={{
                  fontSize: '18px',
                }}
              />
              <MKTypography sx={{ ml: 1, mt: 0.5 }} variant="caption" color="error">
                {(touched.source_account && !!errors.source_account) ? errors.source_account : '\xa0'}
              </MKTypography>
            </MKBox>
            <MKBox>
              <MKTypography fontSize="18px" color="#000">
                Product Name *
              </MKTypography>
              {/* <InputField
                name="model"
                label=""
                value={values.model}
                type={INPUT_TYPE_SELECT}
                onChange={(modelId) => handleChangeModel(modelId, setFieldValue)}
                // setFieldValue={setFieldValue}
                options={modelOptions}
                invalid={touched.model && !!errors.model}
                sx={{
                  fontSize: '16px',
                }}
              /> */}
              <Autocomplete
                // freeSolo
                options={modelOptions}
                fullWidth
                getOptionLabel={(option) => {
                  if (typeof option === 'string') {
                    return option;
                  }
                  return `${option.id_name} (${option.id_code})`;
                }}
                value={autoCompleteValue ? `${autoCompleteValue?.id_name} (${autoCompleteValue?.id_code})` : null}
                onChange={(e, newValue) => {
                  handleChangeModel(newValue?.value, setFieldValue);
                  setAutoCompleteValue(newValue);
                }}
                renderInput={(params) => {
                  // eslint-disable-next-line no-param-reassign
                  params.inputProps.style = { fontSize: '18px' };
                  return (
                    <TextField
                      // placeholder="Search by brand / model / code"
                      {...params}
                    />
                  );
                }}
                filterOptions={multiWordfilterOptions}
              />
              <MKTypography sx={{ ml: 1, mt: 0.5 }} variant="caption" color="error">
                {(touched.model && !!errors.model) ? errors.model : '\xa0'}
              </MKTypography>
            </MKBox>
            <MKBox display="flex" width="100%" mb="1rem">
              <MKBox display="flex" flex={1} mr="1rem" bgColor="#f0f2f5">
                <Image src={modelImage} width="100%" />
              </MKBox>
              <MKBox display="flex" flex={1} flexDirection="column">
                <MKBox mb="1rem">
                  <MKTypography fontSize="18px" color="#000">
                    Brand *
                  </MKTypography>
                  <InputField
                    label=""
                    value={brandName}
                    disabled
                    sx={{
                      fontSize: '16px',
                    }}
                  />
                </MKBox>
                <MKBox>
                  <MKTypography fontSize="18px" color="#000">
                    Condition *
                  </MKTypography>
                  {conditionOptions.map((condition) => (
                    <MKBox onClick={() => handleClickOption('condition', condition.value, setFieldValue)} fontSize="16px" border={`1px solid ${condition.value === values.condition ? 'black' : 'lightgray'}`} borderRadius="5px" display="flex" justifyContent="center" py="0.25rem" mb="0.5rem" style={{ cursor: 'pointer' }}>
                      {condition.label}
                    </MKBox>
                  ))}
                </MKBox>
              </MKBox>
            </MKBox>
            <MKBox>
              <MKTypography fontSize="18px" color="#000">
                Monitor For *
              </MKTypography>
              <MKBox display="flex">
                {monitorTypeOptions.map((monitorType, index) => (
                  <MKBox width="50%" mr={index === 0 && '0.5rem'} onClick={() => handleClickOption('monitor_type', monitorType.value, setFieldValue)} fontSize="16px" border={`1px solid ${monitorType.value === values.monitor_type ? 'black' : 'lightgray'}`} borderRadius="5px" display="flex" justifyContent="center" py="0.25rem" mb="0.5rem" style={{ cursor: 'pointer' }}>
                    {monitorType.label}
                  </MKBox>
                ))}
              </MKBox>
              <MKTypography sx={{ ml: 1, mt: 0.5 }} variant="caption" color="error">
                {(touched.monitor_type && !!errors.monitor_type) ? errors.monitor_type : '\xa0'}
              </MKTypography>
            </MKBox>
            <MKBox>
              <MKTypography fontSize="18px" color="#000">
                Currency
              </MKTypography>
              <InputField
                name="currency"
                label=""
                value={values.currency}
                type={INPUT_TYPE_SELECT}
                setFieldValue={setFieldValue}
                options={currencyOptions}
                invalid={touched.currency && !!errors.currency}
                sx={{
                  fontSize: '16px',
                }}
              />
              <MKTypography sx={{ ml: 1, mt: 0.5 }} variant="caption" color="error">
                {(touched.currency && !!errors.currency) ? errors.currency : '\xa0'}
              </MKTypography>
            </MKBox>
            <MKBox>
              <MKTypography fontSize="18px" color="#000">
                Expected Lowest Price
              </MKTypography>
              <InputField
                name="expected_lowest_price"
                label=""
                value={values.expected_lowest_price}
                handleChange={handleChange}
                handleBlur={handleBlur}
                error={touched.expected_lowest_price && !!errors.expected_lowest_price}
              />
              <MKTypography sx={{ ml: 1, mt: 0.5 }} variant="caption" color="error">
                {(touched.expected_lowest_price && !!errors.expected_lowest_price) ? errors.expected_lowest_price : '\xa0'}
              </MKTypography>
            </MKBox>
            <MKBox>
              <MKTypography fontSize="18px" color="#000">
                Expected Highest Price
              </MKTypography>
              <InputField
                name="expected_highest_price"
                label=""
                value={values.expected_highest_price}
                handleChange={handleChange}
                handleBlur={handleBlur}
                error={touched.expected_highest_price && !!errors.expected_highest_price}
              />
              <MKTypography sx={{ ml: 1, mt: 0.5 }} variant="caption" color="error">
                {(touched.expected_highest_price && !!errors.expected_highest_price) ? errors.expected_highest_price : '\xa0'}
              </MKTypography>
            </MKBox>
            {monitorItem && (
              <MKBox display="flex" justifyContent="center">
                <MKBox display="flex" alignItems="center" style={{ cursor: 'pointer' }} onClick={() => onDelete(setFieldError)}>
                  <DeleteOutlineIcon fontSize="medium" style={{ color: 'red' }} />
                  <MKTypography color="red">Delete Monitor</MKTypography>
                </MKBox>
              </MKBox>
            )}
            <Grid container mb={5}>
              <Grid item xs={12}>
                <MKTypography variant="caption" color="error">
                  {errors.form}
                  &nbsp;
                </MKTypography>
              </Grid>
            </Grid>
          </MKBox>
        );
      }}
    </Formik>
  );
};

EditMonitorForm.propTypes = {
  monitorItem: PropTypes.object,
  models: PropTypes.array,
  onSave: PropTypes.func,
  onDelete: PropTypes.func,
  titleFontSize: PropTypes.string,
  titleColor: PropTypes.string,
};

EditMonitorForm.defaultProps = {
  monitorItem: {},
  models: [],
  onSave: () => {},
  onDelete: () => { },
  titleFontSize: '',
  titleColor: '',
};

export default EditMonitorForm;
